import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TableSortLabel, CircularProgress,
    IconButton, Tooltip
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { axios_tenant } from "../../../../axios";
import OAPagination from "../../../shared/OAWorkflow/components/OAPagination";
import { formatOADateTime } from "../../../shared/OAWorkflow/utils/dateUtils";
import { BRAND_COLORS } from "../../../shared/OAWorkflow/constants/taskStatuses";
import { useDocumentTitle } from "../../../shared/OAWorkflow/utils/useDocumentTitle";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import "./OAWorkflow.css";

const BASE_URL = "/collaboration/workflow";

/**
 * Validation Result page — shows import batches.
 * Route: /collaboration-space/oa-workflow/batches
 * Restricted to Admin + Application Manager.
 */
const OAWorkflowImportBatchList = () => {
    useDocumentTitle("Validation Results");
    const navigate = useNavigate();
    const outerTheme = useTheme();

    const localTheme = React.useMemo(() => createTheme(outerTheme, {
        typography: {
            fontFamily: 'Lato, sans-serif',
        },
    }), [outerTheme]);

    const [batches, setBatches] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);

    // Sorting state
    const [orderBy, setOrderBy] = useState("uploadedAt");
    const [order, setOrder] = useState("desc");

    const fetchBatches = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios_tenant.get(`${BASE_URL}/intake/batches`, {
                params: { page: page + 1, limit: rowsPerPage, sortBy: orderBy, sortOrder: order }
            });
            setBatches(res.data.data || []);
            setTotalRecords(res.data.pagination?.totalRecords || 0);
        } catch (err) {
            console.error("Failed to fetch batches:", err);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, orderBy, order]);

    useEffect(() => {
        fetchBatches();
    }, [fetchBatches]);

    const handleDownloadReport = async (batchId) => {
        try {
            setDownloadingId(batchId);
            const response = await axios_tenant.get(`${BASE_URL}/intake/batches/${batchId}/export`, {
                responseType: 'blob',
                silent: true
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Validation_Result_Batch_${batchId}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (err) {
            console.error("Failed to download validation report:", err);
            alert("Failed to download validation report. It might have been cleared or the batch is too old.");
        } finally {
            setDownloadingId(null);
        }
    };

    const handleDeleteBatch = async (batchId) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this batch?");
        if (!confirmDelete) return;

        try {
            await axios_tenant.delete(`${BASE_URL}/intake/batches/${batchId}`, { silent: true });
            fetchBatches();
        } catch (err) {
            console.error("Failed to delete batch:", err);
            alert("Failed to delete the batch.");
        }
    };

    const handleSort = (column) => {
        if (orderBy === column) {
            setOrder(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setOrderBy(column);
            setOrder("asc");
        }
        setPage(0);
    };

    const headCells = [
        { id: "fileName", label: "File Name", sortable: true },
        { id: "clientName", label: "Client Name", sortable: true },
        { id: "uploadedAt", label: "Date/Time", sortable: true },
        { id: "uploadedBy", label: "Uploaded By", sortable: true },
        { id: "action", label: "Action", sortable: false, align: "center" },
    ];

    return (
        <ThemeProvider theme={localTheme}>
            <Box className="oa-lato-font" sx={{ p: 4, pb: 10 }} data-testid="oa-workflow-batch-list-page">
                <Typography variant="h5" fontWeight={700} sx={{ mb: 3, fontSize: "20px" }}>
                    Validation Result
                </Typography>

                <Box sx={{ position: "relative", width: "100%" }}>
                    <TableContainer
                        sx={{
                            borderRadius: 2,
                            border: "1px solid #E2E8F0",
                            backgroundColor: "#fff"
                        }}
                    >
                        {loading && (
                            <Box sx={{
                                position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                                backgroundColor: "rgba(255,255,255,0.7)",
                                zIndex: 10, display: "flex", justifyContent: "center", alignItems: "center",
                                minHeight: 150
                            }}>
                                <CircularProgress size={32} sx={{ color: BRAND_COLORS.PRIMARY }} />
                            </Box>
                        )}
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {headCells.map((cell) => (
                                        <TableCell
                                            key={cell.id}
                                            align={cell.align || "left"}
                                            sx={{
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                color: "#64748B",
                                                backgroundColor: "#F8FAFC",
                                                borderBottom: "1px solid #E2E8F0",
                                                whiteSpace: "nowrap"
                                            }}
                                            sortDirection={orderBy === cell.id ? order : false}
                                        >
                                            {cell.sortable ? (
                                                <TableSortLabel
                                                    active={orderBy === cell.id}
                                                    direction={orderBy === cell.id ? order : "asc"}
                                                    onClick={() => handleSort(cell.id)}
                                                    IconComponent={() => (
                                                        <span style={{ fontSize: '14px', marginLeft: '4px' }}>
                                                            {orderBy === cell.id ? (order === 'desc' ? '↓' : '↑') : '↓'}
                                                        </span>
                                                    )}
                                                    sx={{
                                                        '&:hover': { color: 'inherit' },
                                                        '&.Mui-active': { color: 'inherit' },
                                                        '& .MuiTableSortLabel-icon': {
                                                            opacity: orderBy === cell.id ? 1 : 0.3,
                                                            color: '#666'
                                                        }
                                                    }}
                                                >
                                                    {cell.label}
                                                </TableSortLabel>
                                            ) : (
                                                cell.label
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {batches.length === 0 ? (
                                    !loading && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                <Typography color="text.secondary" sx={{ py: 4 }}>
                                                    No validation results found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )
                                ) : (
                                    batches.map((batch) => (
                                        <TableRow
                                            key={batch.id}
                                            hover
                                            data-testid={`oa-batch-row-${batch.id}`}
                                            sx={{
                                                "&:hover": { backgroundColor: "#F8FAFC" }
                                            }}
                                        >
                                            <TableCell sx={{ fontSize: "14px", color: "#334155" }}>
                                                {batch.fileName || "—"}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "14px", color: "#334155" }}>
                                                {batch.clientName ? `${batch.clientName} (${batch.tenantCode})` : (batch.tenantCode || "—")}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "14px", color: "#334155" }}>
                                                {batch.uploadedAt ? formatOADateTime(batch.uploadedAt) : "—"}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "14px", color: "#334155" }}>
                                                {batch.uploadedBy || "—"}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                                                    <Tooltip title="Download Report" arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDownloadReport(batch.id)}
                                                            disabled={downloadingId === batch.id}
                                                            sx={{ color: BRAND_COLORS.PRIMARY }}
                                                        >
                                                            {downloadingId === batch.id ? (
                                                                <CircularProgress size={20} color="inherit" />
                                                            ) : (
                                                                <DownloadIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete Batch" arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteBatch(batch.id)}
                                                            sx={{ color: "#64748B" }}
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {batches.length > 0 && (
                        <OAPagination
                            count={totalRecords}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={setPage}
                            onRowsPerPageChange={(val) => { setRowsPerPage(val); setPage(0); }}
                        />
                    )}
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default OAWorkflowImportBatchList;
